import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaDoorOpen,
  FaPlay,
  FaRoute,
  FaVrCardboard
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const videoSrc = '/vr-walkthrough-demo.mp4';
const thumbnailImage = '/vr-walkthrough-thumbnail.jpg';

const featureCards = [
  {
    icon: FaVrCardboard,
    title: 'Realistic VR Exploration',
    description: 'Step into believable spaces designed to feel present, immersive, and easy to understand.'
  },
  {
    icon: FaDoorOpen,
    title: 'Interactive Environment',
    description: 'Engage with presentation points, material cues, and scene details inside the walkthrough.'
  },
  {
    icon: FaRoute,
    title: 'Smooth Navigation',
    description: 'Move through spaces with a polished VR flow built for comfort and clear spatial orientation.'
  },
  {
    icon: FaBuilding,
    title: 'Presentation Ready Experience',
    description: 'Showcase interiors, layouts, and design intent with a premium experience made for clients.'
  }
];

const reasons = [
  'Present spaces with immersive visual context',
  'Help viewers understand scale and layout faster',
  'Create a premium walkthrough without physical site access'
];

const useCases = [
  'Architecture presentations',
  'Interior visualization',
  'Real estate walkthroughs',
  'Client design reviews'
];

const VRWalkthrough: React.FC = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const walkthroughJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'VR Walkthrough',
    applicationCategory: 'DesignApplication',
    url: `${siteUrl}/vr-walkthrough`,
    image: `${siteUrl}${thumbnailImage}`,
    description: 'VR Walkthrough is an immersive virtual reality experience for presenting interiors, spaces, layouts, and architectural visualization projects.',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/contact?service=VR%20Walkthrough%20Demo%20Request`
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="VR Walkthrough - Immersive Virtual Reality Space Presentation"
        description="Explore VR Walkthrough, an immersive virtual reality presentation experience for interiors, architectural visualization, property previews, and client design reviews."
        path="/vr-walkthrough"
        image={thumbnailImage}
        keywords={[
          'VR walkthrough',
          'virtual reality walkthrough',
          'architectural visualization',
          'interior walkthrough',
          'VR property presentation'
        ]}
        jsonLd={walkthroughJsonLd}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />
      <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-red-500/20 blur-3xl pointer-events-none" />
      <div className="absolute right-[-6rem] top-[32rem] h-80 w-80 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-display text-sm font-semibold text-red-400">Virtual Reality Walkthrough Experience</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                VR Walkthrough
                <span className="mt-2 block bg-gradient-to-r from-red-500 via-red-300 to-red-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-gradient">
                  Immersive Virtual Reality Experience
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-400 sm:text-xl">
                Explore realistic spaces in virtual reality with an immersive walkthrough experience designed for
                presentation, interaction, and premium visualization.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact?service=VR%20Walkthrough%20Demo%20Request"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
                >
                  <span>Request a Demo</span>
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
                {['Unity', 'VR', 'Walkthrough', 'Visualization'].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center text-sm font-medium text-gray-300 backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative min-h-[420px] animate-scale-in sm:min-h-[520px] lg:min-h-[640px]">
              <div className="absolute inset-0 rounded-[2.5rem] border border-white/10 bg-[radial-gradient(circle_at_50%_32%,rgba(255,85,85,0.25),transparent_24%),linear-gradient(160deg,rgba(18,18,21,0.94)_0%,rgba(7,7,9,0.96)_52%,rgba(1,1,2,0.98)_100%)] shadow-2xl shadow-black/30" />
              <div className="absolute inset-6 rounded-[2rem] border border-red-500/20 bg-black/30 p-3 backdrop-blur-sm sm:inset-8 sm:p-4">
                <div className="relative flex h-full items-center justify-center overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  {isVideoLoaded ? (
                    <video
                      src={videoSrc}
                      className="max-h-full w-full bg-black object-contain"
                      controls
                      autoPlay
                      playsInline
                      poster={thumbnailImage}
                      preload="auto"
                      onErrorCapture={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsVideoLoaded(true)}
                      className="group relative block h-full w-full overflow-hidden bg-black text-left"
                      aria-label="Play VR Walkthrough demo video"
                    >
                      <img
                        src={thumbnailImage}
                        alt="VR Walkthrough interior experience preview"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-black/15" />
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-300/70 to-transparent" />
                      <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-red-200/40 bg-red-500/20 text-white shadow-2xl shadow-red-500/30 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-red-500/30">
                        <FaPlay className="ml-1 text-2xl" />
                      </span>
                      <span className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full border border-red-500/30 bg-black/65 px-5 py-3 text-sm font-semibold text-red-300 backdrop-blur-md">
                        <FaVrCardboard />
                        Play VR Walkthrough Demo
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Why VR Walkthrough</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Make spaces easier to <span className="text-red-500">experience</span>
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                VR Walkthrough turns static layouts into an interactive presentation where viewers can understand
                depth, flow, materials, and atmosphere from inside the space.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {reasons.map((reason, index) => (
                <div
                  key={reason}
                  className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 font-display font-bold text-red-400">
                    0{index + 1}
                  </div>
                  <p className="font-semibold leading-relaxed text-white">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <span className="font-display text-sm font-semibold text-red-400">Key Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for premium <span className="text-red-500">space visualization</span>
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-transparent group-hover:opacity-100" />
                <feature.icon className="relative text-5xl text-red-500 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="relative mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="relative mt-3 leading-relaxed text-gray-400">{feature.description}</p>
                <div className="absolute right-0 top-0 h-20 w-20 rounded-bl-3xl bg-gradient-to-br from-red-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-10 lg:p-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,85,85,0.20),transparent_30%),radial-gradient(circle_at_75%_80%,rgba(239,68,68,0.15),transparent_34%)]" />
            <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                  <span className="font-display text-sm font-semibold text-red-400">Immersive Experience</span>
                </div>
                <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                  Step inside the space before it exists
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-400">
                  Present lighting, materials, room flow, and spatial relationships through a VR walkthrough made for
                  memorable client-facing visualization.
                </p>
              </div>

              <div className="relative min-h-[320px] rounded-[2rem] border border-red-500/20 bg-black/40 p-8">
                <div className="absolute inset-x-8 top-10 h-px bg-gradient-to-r from-transparent via-red-300/60 to-transparent" />
                <div className="absolute left-1/2 top-16 h-20 w-44 -translate-x-1/2 rounded-full border border-red-500/40 bg-red-500/10 shadow-[0_0_50px_rgba(239,68,68,0.25)]" />
                <div className="absolute inset-x-[15%] bottom-12 h-32 rounded-t-[2rem] border border-red-500/30 bg-red-500/10" />
                <div className="absolute left-[22%] top-[46%] h-2 w-28 rounded-full bg-white/20" />
                <div className="absolute right-[22%] top-[58%] h-2 w-28 rounded-full bg-white/20" />
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md">
                  <FaVrCardboard className="text-red-400" />
                  Spatial Walkthrough
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Use Cases</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Ready for presentation and <span className="text-red-500">exploration</span>
              </h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              A VR walkthrough helps teams communicate spatial ideas clearly, whether the audience is a buyer, client,
              stakeholder, or design reviewer.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {useCases.map((item) => (
              <div
                key={item}
                className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
              >
                <div className="mb-6 h-1.5 w-20 rounded-full bg-gradient-to-r from-red-500 to-red-300" />
                <h3 className="font-display text-2xl font-bold text-white">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 pb-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,85,85,0.22),_transparent_55%)]" />
            <div className="absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-red-500/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                Explore VR Walkthrough
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
                Open the walkthrough page and experience realistic spaces through immersive VR presentation.
              </p>
              <Link
                to="/contact?service=VR%20Walkthrough%20Demo%20Request"
                className="group mt-8 inline-flex items-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
              >
                <span>Request a Demo</span>
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VRWalkthrough;
